/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import {
    SessionCreateDeviceTypeEnum,
    SessionCreateDrmTypeEnum,
    SessionCreateTransportEncodingTypeEnum,
    SessionErrorResponseCodeEnum,
} from 'client-code/session-manager';

import { Constants } from '../../constants';
import { DeviceModel } from '../../device';
import { Channel } from '../../entities/Channel';
import { Session } from '../../entities/Session';
import { ErrorTypes } from '../../error';
import { ServicesClientImpl } from '../../interactors';
import { IServices } from '../../interfaces/IServices';
import { Log, VuLogger } from '../../logging';
import { RetryMechanism } from '../../retrymechanism/RetryMechanism';
import { RetryOptions } from '../../retrymechanism/RetryOptions';
import { Storage } from '../../storage';
import { TSession } from '../../types';
import { TCreateSessionParams } from '../../types/TCreateSessionParams';
import { TInHomeLocation } from '../../types/TInHomeLocation';
import { UniqueIdentifier } from '../../utils';
import { SessionState } from '../enums';
import { ISessionCreateModel } from '../interfaces';
import { StreamingSessionImpl } from './StreamingSessionImpl';

export class IpLinearStreamingSessionImpl extends StreamingSessionImpl {
    private static TAG: string = 'IpLinearStreamingSession';

    private static oIStreamingSession: IpLinearStreamingSessionImpl;

    public static getInstance(): IpLinearStreamingSessionImpl {
        if (!IpLinearStreamingSessionImpl.oIStreamingSession)
            IpLinearStreamingSessionImpl.oIStreamingSession = new IpLinearStreamingSessionImpl();
        return IpLinearStreamingSessionImpl.oIStreamingSession;
    }

    public createStreamingSession(sessionCreateModel: ISessionCreateModel): Promise<void> {
        const { channel, transportEncodingType, playUri } = sessionCreateModel;
        if (!channel) return Promise.resolve();
        const { partnerStationId, linearProviderPartnerId } = channel;
        return new Promise((resolve, reject) => {
            if (!linearProviderPartnerId) {
                this.mError = this.getPlaybackError(
                    ErrorTypes.LIVE_TV_DEVICE_ERROR_MISSING_CONFIG_V904,
                );
                this.mSessionState = SessionState.ERROR;
                return reject();
            }
            // Need to update devicetype, drmtype, transportEncodingType based on actual data
            const sessionParams: TCreateSessionParams = {
                deviceType: SessionCreateDeviceTypeEnum.IptvSmartTv,
                drmType: SessionCreateDrmTypeEnum.Widevine,
                partnerId: linearProviderPartnerId || '',
                partnerServicesCustomerId: DeviceModel.getTiVoSettings().getPartnerCustomerId(),
                partnerStationId: partnerStationId || '',
                transportEncodingType: <SessionCreateTransportEncodingTypeEnum>(
                    transportEncodingType
                ),
            };

            const createSession = this.mServices.createSession.bind(this.mServices);
            RetryMechanism.callServiceWithRetry<TSession>(
                createSession,
                [sessionParams, UniqueIdentifier.getNextSessionNetworkCallId()],
                <RetryOptions>this.SESSION_MANAGER_DEFAULT_RETRY_OPTIONS,
            )
                .then((response: TSession) => {
                    if (
                        response.sessionNetworkCallId === UniqueIdentifier.getSessionNetworkCallId()
                    ) {
                        this.oSession = response.session;
                        VuLogger.setSessionId(this.oSession.sessionId);
                        this.updateTuningUriInChannelData(this.oSession, channel, playUri || '');
                        this.mSessionState = SessionState.SUCCESS;
                        resolve();
                        if (VuLogger.sessionErrorRecoveryFlag) {
                            VuLogger.logStreamingRecovery(
                                VuLogger.StreamingRecoveryType.SESSION_ERROR,
                            );
                            VuLogger.sessionErrorRecoveryFlag = false;
                        }
                    }
                })
                .catch((error: any) => {
                    // service is unreachable
                    if (error.sessionNetworkCallId === UniqueIdentifier.getSessionNetworkCallId()) {
                        const inHomeLocationData: TInHomeLocation = Storage.getInstance().getItem(
                            Storage.Keys.IN_HOME_LOCATION_DATA,
                        );
                        // setting sessionErrorRecoveryFlag to true as it went into recovery
                        VuLogger.sessionErrorRecoveryFlag = true;
                        if (
                            (!error.response ||
                                error.response.data.code ===
                                    SessionErrorResponseCodeEnum.UnknownLocation) &&
                            inHomeLocationData.status !==
                                Constants.IN_HOME_LOCATION_STATUS.UNREACHABLE
                        ) {
                            this.mSessionState = SessionState.UNREACHABLE;
                            reject();
                        } else {
                            if (!error?.response?.data) return reject();
                            VuLogger.sessionErrorRecoveryFlag = false;
                            this.mError = this.getSessionError(error);
                            Diagnostics.error(this.TAG, `{createStreamingSession: ${error}}`);
                            Log.e(this.TAG, `{createStreamingSession: ${error}}`);
                            this.mSessionState = SessionState.ERROR;
                            reject();
                        }
                    }
                });
        });
    }

    private updateTuningUriInChannelData = (data: Session, channel: Channel, playUri: string) => {
        if (data && data.drmType && data.transportEncodingType && data.playlistUrl) {
            const playlistUrl = data.playlistUrl.replace(/^http?:\/\//, 'https://');
            if (playUri !== playlistUrl) {
                VuLogger.playUrl = playlistUrl;
                const drmServerTypeString = data.drmType!;
                const transportEncodingType = data.transportEncodingType!;
                const partnerId = data.partnerId.split('.')[1];
                const urlToUpdate = `ip-abr://${transportEncodingType}/${partnerId}/${drmServerTypeString}/any/${data.playlistUrl}`;
                const services: IServices = new ServicesClientImpl();
                services.updateChannelUrl(channel.channelNumber!, urlToUpdate);
                Diagnostics.info(this.TAG, 'updateTuningUriInChannelData');
                Log.i(this.TAG, 'updateTuningUriInChannelData');
            }
        }
    };
}
